import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import { ArrowLeft, Loader2, AlertTriangle, Clock } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';

import { DispatchCommitmentStrip } from './fulfilment/DispatchCommitmentStrip';
import { OrderReadinessCard } from './fulfilment/OrderReadinessCard';
import { FulfilmentLinesPanel } from './fulfilment/FulfilmentLinesPanel';

// ==================== TYPES ====================

interface TimelineEntry {
  stage: string;
  action: string;
  notes?: string | null;
  userId?: string | null;
  timestamp: string;
}

interface FulfilmentData {
  timeline?: TimelineEntry[];
  discountNotes?: string;
  warehouseLocation?: string;
  advancePaymentStatus?: string;
  advancePaymentNotes?: string;
  manufacturingStage?: string;
  manufacturingNotes?: string;
  qcStage?: string;
  qcResult?: string;
  qcInspector?: string;
  qcNotes?: string;
  qcRework?: string;
  remainingPaymentStatus?: string;
  remainingPaymentNotes?: string;
  fixedVendor?: boolean;
  vendorName?: string;
  poNumber?: string;
  grnNumber?: string;
  grnReceivedQty?: number;
  grnInspectionStatus?: string;
  grnInspector?: string;
  grnNotes?: string;
  paymentStatus?: string;
  paymentNotes?: string;
  allocatedQty?: number;
  dispatchPhotos?: string[];
  carrier?: string;
  vehicleNo?: string;
  driverName?: string;
  shipmentId?: string;
  estimatedDelivery?: string;
  deliveryPhotos?: string[];
  customerInvoiceRef?: string;
  resolutionType?: string;
  adjustmentNotes?: string;
  debitNoteRef?: string;
  creditNoteRef?: string;
  [key: string]: unknown;
}

interface SalesOrder {
  id: string;
  number: string;
  status: string;
  fulfilmentStage?: string | null;
  fulfilmentPath?: string | null;
  fulfilmentData?: FulfilmentData | null;
  total: number;
  subtotal: number;
  contact?: { firstName: string; lastName: string } | null;
  account?: { name: string } | null;
  items: Array<{
    id: string;
    productId?: string;
    description: string;
    quantity: number;
    rate: number;
    amount: number;
    fulfillmentSource: string;
    fulfilledQty?: number;
    unit?: string | null;
    discount?: number;
  }>;
}

// ==================== STAGE DEFINITIONS ====================

const STAGE_LABELS: Record<string, string> = {
  discount: 'Discount',
  inventory: 'Inventory Check',
  advance_payment: 'Advance payment',
  manufacturing: 'Manufacturing',
  qc: 'Quality Control',
  remaining_payment: 'Balance payment',
  procurement: 'Procurement / PO',
  grn_qc: 'GRN & QC',
  payment_check: 'Payment review',
  allocate: 'Allocate Stock',
  dispatch: 'Dispatch',
  transit: 'In Transit',
  delivery: 'Delivery',
  resolution: 'Resolution',
  fulfilled: 'Fulfilled',
};

// ==================== MAIN PAGE ====================

export function SalesOrderFulfilmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [so, setSo] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchSO = useCallback(async () => {
    if (!id) return;
    try {
      setError(null);
      const [statusRes, soRes] = await Promise.all([
        api.get(`/so-fulfilment/${id}/status`),
        api.get(`/sales-orders/${id}`).catch(() => null),
      ]);

      const statusData = statusRes.data?.data ?? {};
      const soData = soRes?.data?.data ?? {};

      const payment = statusData?.payment ?? {};
      const dispatch = statusData?.dispatch ?? {};
      const delivery = statusData?.delivery ?? {};
      const resolution = statusData?.resolution ?? {};
      const shipments = statusData?.shipments ?? [];
      const latestShipment = shipments[0] ?? {};
      const timeline: TimelineEntry[] = [];

      if (dispatch?.dispatchedAt) {
        timeline.push({
          stage: 'dispatch',
          action: 'Order dispatched',
          timestamp: dispatch.dispatchedAt,
          notes: dispatch.lrNumber || undefined,
        });
      }
      if (delivery?.deliveredAt) {
        timeline.push({
          stage: 'delivery',
          action: 'Delivery confirmed',
          timestamp: delivery.deliveredAt,
          notes: delivery.customerInvoiceRef || undefined,
        });
      }
      if (resolution?.resolvedAt) {
        timeline.push({
          stage: 'resolution',
          action: resolution.hasDiscrepancy ? 'Resolved with discrepancy' : 'All clear',
          timestamp: resolution.resolvedAt,
          notes: resolution.notes || undefined,
        });
      }

      const mappedFd: FulfilmentData = {
        timeline,
        advancePaymentStatus: payment.advance50Received === true ? 'received' : payment.isOnHold ? 'on_hold' : '',
        advancePaymentNotes: payment.holdReason || '',
        remainingPaymentStatus: payment.balance50Received === true ? 'received' : '',
        remainingPaymentNotes: '',
        allocatedQty: (statusData?.items ?? []).reduce((sum: number, i: any) => sum + Number(i.fulfilledQty || 0), 0),
        warehouseLocation: statusData?.allocation?.warehouseId || '',
        dispatchPhotos: [],
        shipmentId: dispatch.shipmentId || delivery.shipmentId || undefined,
        carrier: latestShipment.transportPartnerName || '',
        vehicleNo: dispatch.vehicleNumber || latestShipment.vehicleNumber || '',
        driverName: dispatch.driverName || latestShipment.driverName || '',
        lrNumber: dispatch.lrNumber || latestShipment.lrNumber || '',
        estimatedDelivery: latestShipment.estimatedDelivery || '',
        deliveryPhotos: [],
        customerInvoiceRef: delivery.customerInvoiceRef || '',
        receiverName: delivery.receiverName || '',
        receiverPhone: delivery.receiverPhone || '',
        resolutionType: resolution.hasDiscrepancy ? 'qty_mismatch' : 'all_clear',
        adjustmentNotes: resolution.resolutionMethod || '',
        debitNoteRef: resolution.resolutionMethod === 'debit_note' ? resolution.referenceNumber || '' : '',
        creditNoteRef: resolution.resolutionMethod === 'credit_note' ? resolution.referenceNumber || '' : '',
      };

      const mappedSo: SalesOrder = {
        id: statusData.salesOrderId ?? soData.id,
        number: statusData.number ?? soData.number,
        status: statusData.status ?? soData.status,
        fulfilmentPath: null,
        fulfilmentData: mappedFd,
        total: Number(statusData.total ?? soData.total ?? 0),
        subtotal: Number(statusData.total ?? soData.total ?? 0),
        contact: soData.contact || null,
        account: soData.account || null,
        items: (statusData.items ?? soData.items ?? []).map((item: any) => ({
          id: item.id,
          description: item.description,
          quantity: Number(item.quantity || 0),
          rate: Number(item.rate || 0),
          amount: Number(item.amount || 0),
          fulfillmentSource: item.fulfillmentSource || 'in_stock',
          unit: item.product?.unit || null,
          discount: Number(item.discount || 0),
          productId: item.product?.id,
          fulfilledQty: Number(item.fulfilledQty || 0),
        })),
      } as SalesOrder;

      setSo(mappedSo);
    } catch {
      setError('Failed to load sales order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchSO(); }, [fetchSO]);

  const fd: FulfilmentData = (so?.fulfilmentData as FulfilmentData) || {};
  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Loading fulfilment data...</span>
      </div>
    );
  }

  if (error || !so) {
    return (
      <div className="flex flex-col items-center justify-center py-32" style={{ color: 'var(--text-muted)' }}>
        <AlertTriangle size={40} className="mb-2" />
        <p className="text-sm">{error || 'Sales order not found'}</p>
        <button onClick={() => navigate('/sales-orders')} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Back to list</button>
      </div>
    );
  }

  const customerName = so.contact
    ? `${joinPersonName(so.contact.firstName, so.contact.lastName)}`
    : so.account?.name || 'Unknown Customer';

  const STATUS_BADGE: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    confirmed: 'bg-blue-50 text-blue-700',
    processing: 'bg-yellow-50 text-yellow-700',
    shipped: 'bg-indigo-50 text-indigo-700',
    delivered: 'bg-green-50 text-green-700',
    cancelled: 'bg-red-50 text-red-700',
  };

  const timeline: TimelineEntry[] = fd.timeline || [];

  return (
    <div className="mx-auto max-w-4xl">
      {/* Toast */}
      {toast && (
        <div className={`fixed right-6 top-6 z-50 rounded-xl border px-5 py-3 text-sm font-medium shadow-lg ${toast.type === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/sales-orders/${id}`)} className="rounded-lg p-1.5 transition-colors hover:opacity-70" style={{ background: 'var(--bg-muted)' }}>
            <ArrowLeft size={18} style={{ color: 'var(--text-secondary)' }} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{so.number}</h1>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[so.status] || 'bg-gray-100 text-gray-600'}`}>
                {so.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </span>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{customerName} — Fulfilment Manager</p>
          </div>
        </div>
        <span className="text-sm font-medium text-[var(--ink-0)]">{formatINR(so.total)}</span>
      </div>

      {/* Phase 2 plan 12 — dispatch-date commitment (24 h after approval) */}
      {id && <DispatchCommitmentStrip key={id} salesOrderId={id} />}

      {/* Phase 2 (X2): per-line routing replaces the manual stepper. */}
      {id && <OrderReadinessCard soId={id} />}
      <FulfilmentLinesPanel soId={so.id} onRouted={() => { showToast('success', 'Order routing checked'); void fetchSO(); }} />

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="rounded-xl border" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="border-b px-5 py-3" style={{ borderColor: 'var(--border-primary)' }}>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Fulfilment History</h3>
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
            {[...timeline].reverse().map((entry, i) => (
              <div key={i} className="flex items-start gap-4 px-5 py-4">
                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full" style={{ background: 'var(--bg-muted)' }}>
                  <Clock size={13} style={{ color: 'var(--text-muted)' }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}>
                      {STAGE_LABELS[entry.stage] || entry.stage}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(entry.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{entry.action}</p>
                  {entry.notes && <p className="mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>{entry.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
